import styled from '@emotion/styled'
import { breakpoint, themedValue } from '../../..'
import { Sharing } from '../../../buttons/Sharing/Sharing'
import { colors } from '../../../__styling/settings/colors'
import { metrics } from '../../../__styling/settings/metrics'
import { calcRem } from '../../../__styling/style-functions/calc-rem'

export const StyledLiveEventPost = styled('div')(({ theme }) => ({
    padding: theme.margins.md,
    marginBottom: themedValue(theme, {
        sevennews: theme.margins.lg,
        fallback: theme.margins.sm,
    }),
    background: theme.colors.palette.white,
    boxShadow: themedValue(theme, {
        thewest: `0px 2px 3px rgba(0, 0, 0, 0.08)`,
        perthnow: `0px 2px 3px rgba(0, 0, 0, 0.08)`,
        sevennews: `0px 0px 11px rgba(0, 0, 0, 0.25)`,
        fallback: 'none',
    }),

    [breakpoint('sm')]: {
        margin: theme.margins.md,
    },
    [breakpoint('md')]: {
        margin: themedValue(theme, {
            thewest: 0,
            perthnow: 0,
            sevennews: `${theme.margins.md}px ${theme.margins.sm}px`,
        }),

        borderBottom: themedValue(theme, {
            thewest: `1px solid ${colors.thewest.greyQuokka}`,
            perthnow: `1px solid ${colors.thewest.greyQuokka}`,
            sevennews: 'none',
        }),
    },
}))

export const StyledLiveEventTitle = styled('h2')(({ theme }) => ({
    margin: themedValue(theme, {
        sevennews: `${theme.margins.sm}px 0px`,
        fallback: '',
    }),
    fontSize: themedValue(theme, {
        thewest: calcRem(26),
        perthnow: calcRem(26),
        sevennews: calcRem(21),
    }),
    fontFamily: themedValue(theme, {
        thewest: 'inherit',
        perthnow: 'inherit',
        sevennews: theme.fonts.headingAlternative,
    }),
    fontWeight: themedValue(theme, {
        sevennews: 700,
        fallback: 400,
    }),
    color: theme.colors.palette.black,

    lineHeight: calcRem(31),
}))

export const StyledLiveEventArticleBlockContent = styled('div')(({ theme }) => [
    {
        marginTop: metrics.thewest.margins.md,
        '& > div > p': {
            fontFamily: themedValue(theme, {
                thewest: 'inherit',
                perthnow: 'inherit',
                sevennews: theme.fonts.headingAlternative,
            }),
            fontSize: themedValue(theme, {
                thewest: calcRem(16),
                perthnow: calcRem(16),
                sevennews: calcRem(16),
            }),
            fontWeight: themedValue(theme, {
                sevennews: 400,
                fallback: 'inherit',
            }),
            lineHeight: themedValue(theme, {
                sevennews: calcRem(27),
                fallback: calcRem(24),
            }),
        },

        '& > div > blockquote > p': {
            marginLeft: themedValue(theme, {
                sevennews: 0,
                fallback: theme.margins.md,
            }),
            fontSize: themedValue(theme, {
                thewest: calcRem(21),
                perthnow: calcRem(21),
                sevennews: calcRem(18),
            }),
            fontWeight: themedValue(theme, {
                thewest: 400,
                perthnow: 400,
                sevennews: 700,
            }),
            fontFamily: themedValue(theme, {
                sevennews: theme.fonts.headingAlternative,
                fallback: theme.fonts.serif,
            }),

            color: themedValue(theme, {
                sevennews: theme.colors.brand,
                fallback: 'inherit',
            }),
            lineHeight: calcRem(25),
        },
        '& > div > blockquote > div': {
            width: 4,
        },
    },
    theme.kind === 'sevennews' && {
        '& > div > blockquote ': {
            transform: 'translateX(0)',
            marginLeft: 0,
            marginRight: 0,
            width: '100%',
            maxWidth: '100%',
            '& > p': {
                maxWidth: '100%',
            },
        },
    },
])

export const StyledLiveEventSocialIcons = styled(Sharing)(({ theme }) => [
    {
        '&': {
            marginBottom: 0,
        },
        '& > button ': {
            position: 'relative',
            color: themedValue(theme, {
                thewest: colors.thewest.greyKoala,
                perthnow: colors.thewest.greyKoala,
                sevennews: 'inherit',
            }),
            backgroundColor: themedValue(theme, {
                thewest: colors.thewest.greyErmine,
                perthnow: colors.thewest.greyErmine,
                sevennews: 'inherit',
            }),
            border: themedValue(theme, {
                thewest: 'none',
                perthnow: 'none',
                sevennews: 'solid 1px',
            }),
            width: themedValue(theme, {
                thewest: calcRem(32),
                perthnow: calcRem(32),
                sevennews: calcRem(40),
            }),
            height: themedValue(theme, {
                thewest: calcRem(32),
                perthnow: calcRem(32),
                sevennews: calcRem(40),
            }),

            [breakpoint('md')]: {
                width: themedValue(theme, {
                    thewest: calcRem(24),
                    perthnow: calcRem(24),
                    sevennews: calcRem(40),
                }),
                height: themedValue(theme, {
                    thewest: calcRem(24),
                    perthnow: calcRem(24),
                    sevennews: calcRem(40),
                }),
            },
        },
        '& > button:hover ': {
            color: themedValue(theme, {
                thewest: colors.thewest.greyKoala,
                perthnow: colors.thewest.greyKoala,
                sevennews: theme.colors.brand,
            }),
            backgroundColor: themedValue(theme, {
                thewest: colors.thewest.greyDolphin,
                perthnow: colors.thewest.greyElephant,
                sevennews: 'none',
            }),
            border: themedValue(theme, {
                thewest: 'none',
                perthnow: 'none',
                sevennews: `solid ${theme.colors.brand} 1px`,
            }),
        },
        '& > button:active ': {
            color: themedValue(theme, {
                thewest: colors.thewest.greyKoala,
                perthnow: colors.thewest.greyKoala,
                sevennews: 'inherit',
            }),
            backgroundColor: themedValue(theme, {
                thewest: colors.thewest.greyQuokka,
                perthnow: colors.thewest.greyQuokka,
                sevennews: 'inherit',
            }),
            border: 'none',
        },
        '& > button.gtm-clipboard-share-button:active:before': {
            opacity: 1,
            transition: '0s',
        },
        '& > button.gtm-clipboard-share-button:before': {
            position: 'absolute',
            top: calcRem(-35),
            left: calcRem(-31),
            width: calcRem(98),
            opacity: 0,
            padding: calcRem(6),
            content: '"✓ Link Copied "',
            transition: 'opacity 1.7s cubic-bezier(.0,.05,1,-0.24)',
            fontFamily: theme.fonts.sansSerif,
            fontSize: themedValue(theme, {
                thewest: calcRem(13),
                perthnow: calcRem(13),
                sevennews: calcRem(12),
            }),
            fontWeight: 600,
            background: theme.colors.palette.black,
            color: theme.colors.palette.white,
        },
    },
    theme.kind === 'sevennews' && {
        '&::before': {
            display: 'none',
        },
        '& > button.gtm-facebook-share-button > svg': {
            top: '53%',
        },
    },
])
